import type {
  ContactValue,
  CoordinateValue,
  JsonSchemaType,
  PhoneNumberEntry,
  Position,
  StatusColor,
  StatusOption,
  StatusRange,
  StatusValue,
} from '@gmn-portal/backend';
import { Check, Copy, Fuel, Lightbulb, Plane, Stethoscope, TriangleAlert } from 'lucide-react';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { useIntl } from '@/core/i18n/use-intl';
import type { Object as PortalObject } from '@/features/shared/objects/types';
import { ContactEmailLink } from '@/shared/components/metadata/contact-email-link';
import { ContactPhoneLink } from '@/shared/components/metadata/contact-phone-link';
import { RelationDisplayField } from '@/shared/components/metadata/relation-display-field';
import { Badge, Button } from '@/shared/components/ui';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/collapsible';
import { useCoordinateFormat } from '@/shared/hooks/use-coordinate-format';
import { cn, formatCoordinateForCopy, formatStoredCoordinatePart } from '@/shared/utils';
import { getFieldPermission } from '@/shared/utils/metadata';
import { getCurrentStatusResolution } from '@/shared/utils/status';

const widthClasses: Record<string, string> = {
  '33': 'col-span-6 md:col-span-2',
  '50': 'col-span-6 md:col-span-3',
  '100': 'col-span-6',
};

function getReadFieldCardClasses(highlighted: boolean) {
  return cn(
    'relative rounded-xl border p-4',
    highlighted
      ? 'overflow-hidden border-yellow-400/70 bg-yellow-50/50 shadow-sm dark:border-yellow-500/40 dark:bg-yellow-500/10 after:absolute after:inset-y-0 after:left-0 after:w-1 after:bg-yellow-400 dark:after:bg-yellow-500'
      : 'border-border bg-card'
  );
}

function getReadFieldLabelClasses(highlighted: boolean) {
  return cn(
    'mb-1 text-xs',
    highlighted
      ? 'font-semibold text-yellow-800 dark:text-yellow-200'
      : 'font-medium text-muted-foreground'
  );
}

function getLocalizedFieldLabel(
  locale: string,
  name: string,
  metadataSchema: JsonSchemaType
): string {
  const fieldDef = metadataSchema.properties?.[name];
  const fallback = name;
  if (!fieldDef) return fallback;
  if (locale === 'en-US' && fieldDef.description_en && fieldDef.description_en.trim() !== '') {
    return fieldDef.description_en;
  }
  return fieldDef.description || fallback;
}

function formatDateValue(value: unknown, includeTime?: boolean): string {
  if (!value) return '-';
  const dt =
    value instanceof Date
      ? DateTime.fromJSDate(value)
      : typeof value === 'string'
        ? DateTime.fromISO(value)
        : null;
  if (!dt || !dt.isValid) return String(value);
  return includeTime ? dt.toFormat('dd.MM.yyyy HH:mm') : dt.toFormat('dd.MM.yyyy');
}

const statusIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  fuel: Fuel,
  helideck: Plane,
  warning: TriangleAlert,
  check: Check,
  lightning: Lightbulb,
  medical: Stethoscope,
};

const statusDotClasses: Record<StatusColor, string> = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
};

const statusIconTextClasses: Record<StatusColor, string> = {
  green: 'text-green-600 dark:text-green-400',
  yellow: 'text-yellow-600 dark:text-yellow-400',
  red: 'text-red-600 dark:text-red-400',
};

function StatusPill({ color, label, icon }: { color: StatusColor; label: string; icon?: string }) {
  const IconComponent = icon ? statusIconMap[icon] : null;
  return (
    <span className="inline-flex items-center gap-2">
      {IconComponent ? (
        <IconComponent
          className={cn('size-4', statusIconTextClasses[color])}
          data-testid={`status-indicator-icon-${color}`}
        />
      ) : (
        <span
          className={cn('h-2.5 w-2.5 rounded-full', statusDotClasses[color])}
          data-testid={`status-indicator-dot-${color}`}
        />
      )}
      <span className="text-sm font-medium">{label}</span>
    </span>
  );
}

function getStatusOptionLabel(locale: string, opt: StatusOption) {
  return locale === 'de-DE' ? opt.labelDe : opt.labelEn || opt.labelDe;
}

function resolveStatusOption(
  statusOptions: StatusOption[],
  color?: StatusColor,
  optionId?: string
) {
  if (optionId) {
    const byId = statusOptions.find((o) => o.id === optionId);
    if (byId) return byId;
  }

  if (!color) return null;
  return statusOptions.find((o) => o.color === color) ?? null;
}

function ensureStatusOptionsWithIds(statusOptions: StatusOption[]) {
  return statusOptions.map((opt, index) => ({
    ...opt,
    id: opt.id?.trim() || `status-opt-${opt.color}-${index + 1}`,
  }));
}

function safeStatusValue(value: unknown): StatusValue | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as StatusValue;
  if (v.mode !== 'manual' && v.mode !== 'scheduled') return null;
  return v;
}

function safePositionsValue(value: unknown): Position[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (row): row is Position =>
        row && typeof row === 'object' && typeof (row as any).id === 'string'
    )
    .map((row) => ({
      id: row.id,
      label: row.label ?? '',
      latitude: row.latitude ?? undefined,
      longitude: row.longitude ?? undefined,
      status: row.status,
    }));
}

function safePhoneNumbersValue(value: unknown): PhoneNumberEntry[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (row): row is PhoneNumberEntry =>
        row && typeof row === 'object' && typeof (row as PhoneNumberEntry).id === 'string'
    )
    .map((row) => ({
      id: row.id,
      name: row.name ?? '',
      phone: row.phone ?? undefined,
    }));
}

function safeCoordinateValue(value: unknown): CoordinateValue | null {
  if (!value || typeof value !== 'object') return null;
  return value as CoordinateValue;
}

function safeContactValue(value: unknown): Partial<ContactValue> | null {
  if (!value || typeof value !== 'object') return null;
  return value as Partial<ContactValue>;
}

function sortRanges(ranges: StatusRange[]): StatusRange[] {
  return [...ranges].sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
}

function formatDateRange(startDate: string, endDate: string) {
  const start = formatDateValue(startDate, false);
  const end = formatDateValue(endDate, false);
  return `${start} – ${end}`;
}

type MetadataReadViewProps = {
  metadataSchema: JsonSchemaType;
  values?: Record<string, unknown> | null;
  fieldNames: string[];
  groupKey?: string | null;
  className?: string;
  currentObject?: PortalObject | null;
  canReadObjects?: boolean;
  domain?: 'gmn' | 'customer' | null;
};

export function MetadataReadView({
  metadataSchema,
  values,
  fieldNames,
  groupKey,
  className,
  currentObject,
  canReadObjects,
  domain,
}: MetadataReadViewProps) {
  const { t, locale } = useIntl();
  const coordinateFormat = useCoordinateFormat();

  const copyCoordinateText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('metadata.positions.copySuccess'));
    } catch {
      toast.error(t('metadata.positions.copyError'));
    }
  };

  const rows = useMemo(() => {
    const result: Array<
      | { kind: 'section'; key: string; label: string }
      | {
          kind: 'field';
          key: string;
          label: string;
          width: string;
          type: string;
          highlighted: boolean;
          value: unknown;
        }
    > = [];

    for (const key of fieldNames) {
      const fieldDef = metadataSchema.properties?.[key];
      if (!fieldDef) continue;

      const permission = getFieldPermission(fieldDef, groupKey ?? null);
      if (!permission.read) continue;

      const label = getLocalizedFieldLabel(locale, key, metadataSchema);
      if (fieldDef.type === 'section') {
        result.push({ kind: 'section', key, label });
        continue;
      }

      const width = fieldDef.width || '100';
      result.push({
        kind: 'field',
        key,
        label,
        width,
        type: fieldDef.type,
        highlighted: fieldDef.highlighted === true,
        value: values?.[key],
      });
    }

    return result;
  }, [fieldNames, groupKey, locale, metadataSchema, values]);

  return (
    <div className={cn('grid grid-cols-6 gap-4', className)}>
      {rows.map((row) => {
        if (row.kind === 'section') {
          return (
            <div
              key={row.key}
              className="col-span-6 mt-2 mb-1 pb-2 border-b border-border"
              data-testid={`metadata-read-section-${row.key}`}
            >
              <h4 className="text-sm font-semibold text-foreground">{row.label}</h4>
            </div>
          );
        }

        const complex =
          row.type === 'contact' ||
          row.type === 'coordinate' ||
          row.type === 'positions' ||
          row.type === 'phoneNumbers' ||
          row.type === 'status' ||
          row.type === 'relation-display';
        const width = complex ? '100' : row.width;
        const widthClass = widthClasses[width] || 'col-span-6';

        if (row.type === 'relation-display') {
          return (
            <div key={row.key} className={widthClass}>
              <RelationDisplayField
                fieldKey={row.key}
                label={row.label}
                metadataSchema={metadataSchema}
                object={currentObject}
                canReadObjects={canReadObjects}
                domain={domain}
              />
            </div>
          );
        }

        return (
          <div
            key={row.key}
            className={widthClass}
            data-testid={`metadata-read-field-${row.key}`}
            data-highlighted={row.highlighted ? 'true' : 'false'}
          >
            <div className={getReadFieldCardClasses(row.highlighted)}>
              <div className={getReadFieldLabelClasses(row.highlighted)}>{row.label}</div>

              {row.type === 'string' && (
                <div className="text-sm text-foreground whitespace-pre-wrap wrap-break-words">
                  {typeof row.value === 'string' && row.value.trim() !== '' ? row.value : '-'}
                </div>
              )}

              {row.type === 'number' && (
                <div className="text-sm text-foreground">
                  {typeof row.value === 'number'
                    ? String(row.value)
                    : typeof row.value === 'string' && row.value.trim() !== ''
                      ? row.value
                      : '-'}
                </div>
              )}

              {row.type === 'boolean' && (
                <div className="pt-1">
                  <Badge variant="outline-solid">
                    {row.value === true
                      ? t('common.yes')
                      : row.value === false
                        ? t('common.no')
                        : '-'}
                  </Badge>
                </div>
              )}

              {row.type === 'date' && (
                <div className="text-sm text-foreground">
                  {formatDateValue(
                    row.value,
                    metadataSchema.properties?.[row.key]?.format === 'date-time'
                  )}
                </div>
              )}

              {row.type === 'contact' &&
                (() => {
                  const contact = safeContactValue(row.value);
                  if (!contact) return <div className="text-sm text-muted-foreground">-</div>;
                  const hasFallback = Boolean(
                    contact.fallbackContact?.name ||
                      contact.fallbackContact?.email ||
                      contact.fallbackContact?.phone1 ||
                      contact.fallbackContact?.phone2
                  );
                  return (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="text-sm">
                          <div className="text-xs text-muted-foreground">
                            {t('metadata.contact.name')}
                          </div>
                          <div className="font-medium">{contact.name || '-'}</div>
                        </div>
                        <div className="text-sm">
                          <div className="text-xs text-muted-foreground">
                            {t('metadata.contact.email')}
                          </div>
                          <div className="font-medium">
                            <ContactEmailLink value={contact.email} />
                          </div>
                        </div>
                        <div className="text-sm">
                          <div className="text-xs text-muted-foreground">
                            {t('metadata.contact.phone1')}
                          </div>
                          <div className="font-medium">
                            <ContactPhoneLink value={contact.phone1} />
                          </div>
                        </div>
                        <div className="text-sm">
                          <div className="text-xs text-muted-foreground">
                            {t('metadata.contact.phone2')}
                          </div>
                          <div className="font-medium">
                            <ContactPhoneLink value={contact.phone2} />
                          </div>
                        </div>
                      </div>

                      {hasFallback && (
                        <Collapsible>
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-medium text-muted-foreground">
                              {t('metadata.contact.fallbackContact')}
                            </div>
                            <CollapsibleTrigger asChild>
                              <button
                                type="button"
                                className="group text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                              >
                                <span className="group-data-[state=open]:hidden">
                                  {t('common.show')}
                                </span>
                                <span className="hidden group-data-[state=open]:inline">
                                  {t('common.hide')}
                                </span>
                              </button>
                            </CollapsibleTrigger>
                          </div>
                          <CollapsibleContent className="mt-2">
                            <div className="rounded-lg border border-border bg-muted/20 p-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="text-sm">
                                  <div className="text-xs text-muted-foreground">
                                    {t('metadata.contact.name')}
                                  </div>
                                  <div className="font-medium">
                                    {contact.fallbackContact?.name || '-'}
                                  </div>
                                </div>
                                <div className="text-sm">
                                  <div className="text-xs text-muted-foreground">
                                    {t('metadata.contact.email')}
                                  </div>
                                  <div className="font-medium">
                                    <ContactEmailLink value={contact.fallbackContact?.email} />
                                  </div>
                                </div>
                                <div className="text-sm">
                                  <div className="text-xs text-muted-foreground">
                                    {t('metadata.contact.phone1')}
                                  </div>
                                  <div className="font-medium">
                                    <ContactPhoneLink value={contact.fallbackContact?.phone1} />
                                  </div>
                                </div>
                                <div className="text-sm">
                                  <div className="text-xs text-muted-foreground">
                                    {t('metadata.contact.phone2')}
                                  </div>
                                  <div className="font-medium">
                                    <ContactPhoneLink value={contact.fallbackContact?.phone2} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </div>
                  );
                })()}

              {row.type === 'coordinate' &&
                (() => {
                  const coordinate = safeCoordinateValue(row.value);
                  if (!coordinate) {
                    return <div className="text-sm text-muted-foreground">-</div>;
                  }

                  return (
                    <div className="flex flex-col space-y-3">
                      <div className="flex flex-wrap items-end justify-between gap-3">
                        <div className="grid grid-cols-2 gap-4 sm:gap-6 flex-1">
                          <div>
                            <div className="text-xs text-muted-foreground mb-1">
                              {t('metadata.positions.latitude')}
                            </div>
                            <div className="text-sm font-medium">
                              {formatStoredCoordinatePart(
                                coordinate.latitude,
                                'latitude',
                                coordinateFormat
                              ) || '-'}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground mb-1">
                              {t('metadata.positions.longitude')}
                            </div>
                            <div className="text-sm font-medium">
                              {formatStoredCoordinatePart(
                                coordinate.longitude,
                                'longitude',
                                coordinateFormat
                              ) || '-'}
                            </div>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="shrink-0 h-8"
                          onClick={() =>
                            copyCoordinateText(
                              formatCoordinateForCopy(coordinate, coordinateFormat)
                            )
                          }
                        >
                          <Copy className="size-3.5 mr-1.5" />
                          {t('metadata.positions.copy')}
                        </Button>
                      </div>
                    </div>
                  );
                })()}

              {row.type === 'positions' &&
                (() => {
                  const positions = safePositionsValue(row.value);
                  const statusLabel =
                    (locale === 'en-US'
                      ? (metadataSchema.properties?.[row.key]?.statusLabelEn as
                          | string
                          | undefined) ||
                        (metadataSchema.properties?.[row.key]?.statusLabel as string | undefined)
                      : (metadataSchema.properties?.[row.key]?.statusLabel as
                          | string
                          | undefined)) ?? '';

                  if (positions.length === 0) {
                    return (
                      <div className="text-sm text-muted-foreground">
                        {t('metadata.positions.noPositions')}
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-3">
                      <div className="px-3 hidden md:grid md:grid-cols-[1.4fr_1fr_1fr_auto] gap-4 text-xs font-medium text-muted-foreground">
                        <div>{t('metadata.positions.label')}</div>
                        <div>{t('metadata.positions.latitude')}</div>
                        <div>{t('metadata.positions.longitude')}</div>
                        <div className="text-right w-[32px]"></div>
                      </div>
                      <div className="space-y-2">
                        {positions.map((pos, idx) => (
                          <div
                            key={pos.id ?? idx}
                            className="rounded-lg border border-border bg-card p-3 shadow-sm"
                          >
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-[1.4fr_1fr_1fr_auto] md:items-center">
                              <div>
                                <div className="text-xs text-muted-foreground md:hidden mb-1">
                                  {t('metadata.positions.label')}
                                </div>
                                <div className="text-sm font-medium">{pos.label || '-'}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-3 md:contents">
                                <div>
                                  <div className="text-xs text-muted-foreground md:hidden mb-1">
                                    {t('metadata.positions.latitude')}
                                  </div>
                                  <div className="text-sm">
                                    {formatStoredCoordinatePart(
                                      pos.latitude,
                                      'latitude',
                                      coordinateFormat
                                    ) || '-'}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs text-muted-foreground md:hidden mb-1">
                                    {t('metadata.positions.longitude')}
                                  </div>
                                  <div className="text-sm">
                                    {formatStoredCoordinatePart(
                                      pos.longitude,
                                      'longitude',
                                      coordinateFormat
                                    ) || '-'}
                                  </div>
                                </div>
                              </div>
                              <div className="flex md:justify-end mt-2 md:mt-0">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() =>
                                    copyCoordinateText(
                                      formatCoordinateForCopy(pos, coordinateFormat)
                                    )
                                  }
                                  title={t('metadata.positions.copy')}
                                >
                                  <span className="sr-only">{t('metadata.positions.copy')}</span>
                                  <Copy className="size-4 text-muted-foreground" />
                                </Button>
                              </div>
                            </div>
                            {statusLabel && (
                              <div className="mt-3 pt-3 border-t border-border flex flex-wrap items-center justify-between gap-2 text-sm">
                                <span className="text-muted-foreground">{statusLabel}</span>
                                <Badge variant={pos.status ? 'default' : 'secondary'}>
                                  {pos.status ? t('common.yes') : t('common.no')}
                                </Badge>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

              {row.type === 'phoneNumbers' &&
                (() => {
                  const phoneNumbers = safePhoneNumbersValue(row.value);

                  if (phoneNumbers.length === 0) {
                    return (
                      <div className="text-sm text-muted-foreground">
                        {t('metadata.phoneNumbers.noPhoneNumbers')}
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-3">
                      <div className="px-3 hidden md:grid md:grid-cols-2 gap-4 text-xs font-medium text-muted-foreground">
                        <div>{t('metadata.phoneNumbers.name')}</div>
                        <div>{t('metadata.phoneNumbers.phone')}</div>
                      </div>
                      <div className="space-y-2">
                        {phoneNumbers.map((entry, idx) => (
                          <div
                            key={entry.id ?? idx}
                            className="rounded-lg border border-border bg-card p-3 shadow-sm"
                          >
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:items-center">
                              <div>
                                <div className="text-xs text-muted-foreground md:hidden mb-1">
                                  {t('metadata.phoneNumbers.name')}
                                </div>
                                <div className="text-sm font-medium">{entry.name || '-'}</div>
                              </div>
                              <div>
                                <div className="text-xs text-muted-foreground md:hidden mb-1">
                                  {t('metadata.phoneNumbers.phone')}
                                </div>
                                <div className="text-sm font-medium">
                                  <ContactPhoneLink value={entry.phone} />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

              {row.type === 'status' &&
                (() => {
                  const statusOptionsRaw: StatusOption[] =
                    (metadataSchema.properties?.[row.key]?.statusOptions as
                      | StatusOption[]
                      | undefined) ?? [];
                  const statusOptions = ensureStatusOptionsWithIds(statusOptionsRaw);
                  const statusIcon = metadataSchema.properties?.[row.key]?.icon as
                    | string
                    | undefined;
                  const statusValue = safeStatusValue(row.value);

                  const currentResolution = getCurrentStatusResolution(statusValue);
                  const currentColor = currentResolution.color;
                  const currentOpt = resolveStatusOption(
                    statusOptions,
                    currentColor,
                    currentResolution.optionId
                  );
                  const currentLabel = currentOpt
                    ? getStatusOptionLabel(locale, currentOpt)
                    : currentColor;

                  if (!statusValue) {
                    return (
                      <div className="space-y-2">
                        <StatusPill color={currentColor} label={currentLabel} icon={statusIcon} />
                      </div>
                    );
                  }

                  const modeLabel =
                    statusValue.mode === 'manual'
                      ? t('metadata.status.modeManual')
                      : t('metadata.status.modeScheduled');

                  const defaultOpt = resolveStatusOption(
                    statusOptions,
                    statusValue.defaultStatus,
                    statusValue.defaultStatusOptionId
                  );
                  const defaultLabel = defaultOpt
                    ? getStatusOptionLabel(locale, defaultOpt)
                    : statusValue.defaultStatus;

                  const ranges = statusValue.ranges ? sortRanges(statusValue.ranges) : [];

                  return (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <Badge variant="outline-solid">{modeLabel}</Badge>
                        <StatusPill color={currentColor} label={currentLabel} icon={statusIcon} />
                      </div>

                      {statusValue.mode === 'scheduled' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <div className="text-xs text-muted-foreground">
                              {t('metadata.status.defaultStatus')}
                            </div>
                            <div className="text-sm font-medium">{defaultLabel}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">
                              {t('metadata.status.ranges')}
                            </div>
                            <div className="text-sm font-medium">{ranges.length}</div>
                          </div>
                        </div>
                      )}

                      {statusValue.mode === 'scheduled' && ranges.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-muted-foreground">
                            {t('metadata.status.ranges')}
                          </div>
                          <div className="space-y-2">
                            {ranges.map((r) => {
                              const duringOpt = resolveStatusOption(
                                statusOptions,
                                r.statusDuring,
                                r.statusDuringOptionId
                              );
                              const duringLabel = duringOpt
                                ? getStatusOptionLabel(locale, duringOpt)
                                : r.statusDuring;
                              const fallbackOpt = resolveStatusOption(
                                statusOptions,
                                r.fallbackAfter,
                                r.fallbackAfterOptionId
                              );
                              const fallbackLabel = fallbackOpt
                                ? getStatusOptionLabel(locale, fallbackOpt)
                                : r.fallbackAfter;
                              return (
                                <div
                                  key={r.id}
                                  className="rounded-lg border border-border bg-muted/20 p-2"
                                >
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="text-sm font-medium">
                                      {formatDateRange(r.startDate, r.endDate)}
                                    </div>
                                    <StatusPill
                                      color={r.statusDuring}
                                      label={duringLabel}
                                      icon={statusIcon}
                                    />
                                  </div>
                                  {fallbackLabel && (
                                    <div className="text-xs text-muted-foreground mt-1">
                                      {t('metadata.status.fallbackAfter')}:{' '}
                                      <span className="font-medium">{fallbackLabel}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
            </div>
          </div>
        );
      })}
    </div>
  );
}
